import { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  MenuItem,
  ListItemText,
  Grid,
  Button,
  Pagination,
  PaginationItem,
} from '@mui/material';

import { TableHeadCell } from '../table-cells/TableCells';
import AgentTableRow from './AgentsTableRow';
import Search from '../UI/search/Search';
import Icon from '../Icon';
import MultiSelect from '../multi-select/MultiSelect';
import Checkbox from '../checkbox/Checkbox';
import Loader from '../UI/loader/Loader';

import useSortable from '../../hooks/useSortable';
import {
  useGetAllAgentsQuery,
  useGetProfilesQuery,
  useGetAuthenticationQuery,
} from '../../services/ADapi';
import { getProfileName } from '../../utils/getProfileName';

import { PreviousPageIcon, NextPageIcon } from '../../PaginationIcons';

const filterAgents = (agents, searchQuery = '') => {
  const lowerCaseSearchQuery = searchQuery.toLowerCase();

  if (agents) {
    return agents.filter(({ hostname }) => {
      return hostname
        ? hostname.toLowerCase().includes(lowerCaseSearchQuery)
        : false;
    });
  }

  return [];
};

const MenuProps = {
  PaperProps: {
    style: {
      width: 230,
      padding: 0,
      boxShadow: '0 17px 33px -2px rgba(28, 39, 49, 0.05)',
    },
  },
  MenuListProps: {
    sx: {
      pt: 1,
    },
  },
  TransitionProps: { timeout: 0 },
};

const MultiSelectProps = {
  padding: 0,
  border: 0,
  marginLeft: '8px',
};

const SelectProps = {
  '&.MuiFormControl-root': { width: 'auto', verticalAlign: 'middle' },
};

const primaryTypographyProps = {
  variant: 'body1',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  backgroundColor: 'none',
};

const setSelectedValues = (selected) => {
  return selected.length > 0 ? selected.length : 'All';
};

const AgentsTable = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    data: { user_id },
  } = useGetAuthenticationQuery();
  const { data: profiles = [] } = useGetProfilesQuery(user_id);

  const url = `/agents/rest_api/v1/users/${user_id}/agentinfo/?page=1`;

  const { data: allResults = [], isLoading, error } = useGetAllAgentsQuery(url);

  const [agentOsIds, setAgentOsIds] = useState([]);
  const [selectedOSs, setSelectedOSs] = useState([]);

  const [osFilterState, setOsFilterState] = useState(false);
  const [profileFilterState, setProfileFilterState] = useState(false);

  const [profileIds, setProfileIds] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);

  const agentOSs = useMemo(() => {
    const OSs = new Set();

    allResults.forEach((agent) => {
      OSs.add(agent.os_info);
    });

    return OSs;
  }, [allResults]);

  const filteredAgents = useMemo(() => {
    let result = filterAgents(allResults, searchQuery);

    if (selectedProfiles.length > 0) {
      result = result.filter((agent) =>
        selectedProfiles.includes(agent.profile)
      );
    }

    if (selectedOSs.length > 0) {
      result = result.filter((agent) => selectedOSs.includes(agent.os_info));
    }

    return result;
  }, [allResults, searchQuery, selectedProfiles, selectedOSs]);

  const { result: sortedAgents, requestSort } = useSortable(filteredAgents);

  const ITEMS_PER_PAGE = 5;
  const paginatedAgents = useMemo(() => {
    return sortedAgents.slice(
      (page - 1) * ITEMS_PER_PAGE,
      page * ITEMS_PER_PAGE
    );
  }, [sortedAgents, page]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedProfiles, selectedOSs]);

  const totalPages = Math.ceil(sortedAgents.length / ITEMS_PER_PAGE);

  useEffect(() => {
    if (page > 1 && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const lastSeenHandler = () => {
    requestSort('last_seen');
    setPage(1);
  };

  const clearSelectedProfiles = () => {
    setProfileIds([]);
  };

  const clearSelectedOsIds = () => {
    setAgentOsIds([]);
  };

  const profileChangeHandler = (e) => {
    setProfileIds(e.target.value);
  };

  const renderFilteredAgentsWithinProfiles = () => {
    setSelectedProfiles(profileIds);
    setProfileFilterState(false);
  };

  const onProfileFilterHandler = () => {
    setProfileFilterState(true);
  };

  const closeProfileFilter = () => {
    setProfileFilterState(false);
  };

  const osChangeHandler = (e) => {
    setAgentOsIds(e.target.value);
  };

  const renderFilteredAgents = () => {
    setSelectedOSs(agentOsIds);
    setOsFilterState(false);
  };

  const osFilterStateHandler = () => {
    setOsFilterState(true);
  };

  const closeOsFilter = () => {
    setOsFilterState(false);
  };

  const pageHandler = (_e, value) => {
    setPage(value);
  };

  const searchHandler = (e) => {
    setSearchQuery(e.target.value);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Typography variant="body1">
        {error?.data?.detail ?? 'An error occurred while loading Agents'}
      </Typography>
    );
  }

  return (
    <>
      <Search placeholder="Agent" value={searchQuery} cb={searchHandler} />
      <TableContainer sx={{ pt: 3 }}>
        <Table sx={{ minWidth: '900px' }}>
          <TableHead>
            <TableRow>
              <TableHeadCell sx={{ width: '213px', pr: 0 }}>
                Device
              </TableHeadCell>
              <TableHeadCell align="left" sx={{ width: '213px', p: '16px 0' }}>
                Policy:
                <MultiSelect
                  value={profileIds}
                  multiple
                  open={profileFilterState}
                  onOpen={onProfileFilterHandler}
                  onClose={closeProfileFilter}
                  onChange={profileChangeHandler}
                  MenuProps={MenuProps}
                  displayEmpty
                  renderValue={setSelectedValues}
                  sx={SelectProps}
                  style={MultiSelectProps}
                >
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      <Checkbox
                        checked={profileIds.some((p) => p === profile.id)}
                      />
                      <ListItemText
                        primary={profile.name}
                        primaryTypographyProps={primaryTypographyProps}
                      />
                    </MenuItem>
                  ))}
                  <Grid container spacing={1} sx={{ pr: 1, pl: 1 }}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        onClick={clearSelectedProfiles}
                        fullWidth
                        sx={{
                          // TODO: get rid of !important
                          padding: '4px 16px !important',
                          fontSize: '14px !important',
                        }}
                      >
                        Clear
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        onClick={renderFilteredAgentsWithinProfiles}
                        fullWidth
                        sx={{
                          // TODO: get rid of !important
                          padding: '4px 16px !important',
                          fontSize: '14px !important',
                        }}
                      >
                        Show
                      </Button>
                    </Grid>
                  </Grid>
                </MultiSelect>
              </TableHeadCell>
              <TableHeadCell align="left" sx={{ width: '213px', p: '16px 0' }}>
                OS:
                <MultiSelect
                  value={agentOsIds}
                  multiple
                  open={osFilterState}
                  onOpen={osFilterStateHandler}
                  onClose={closeOsFilter}
                  onChange={osChangeHandler}
                  displayEmpty
                  renderValue={setSelectedValues}
                  MenuProps={MenuProps}
                  style={MultiSelectProps}
                  sx={SelectProps}
                >
                  {Array.from(agentOSs).map((os) => (
                    <MenuItem key={os} value={os} sx={{ pl: 1 }}>
                      <Checkbox
                        checked={agentOsIds.some((oSystem) => oSystem === os)}
                      />
                      <ListItemText
                        primary={os}
                        primaryTypographyProps={primaryTypographyProps}
                      />
                    </MenuItem>
                  ))}
                  <Grid container spacing={1} sx={{ p: 1 }}>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        sx={{
                          width: '100%',
                          padding: '4px 16px !important',
                          fontSize: '14px !important',
                        }}
                        onClick={clearSelectedOsIds}
                      >
                        Clear
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        sx={{
                          width: '100%',
                          padding: '4px 16px !important',
                          fontSize: '14px !important',
                        }}
                        onClick={renderFilteredAgents}
                      >
                        Show
                      </Button>
                    </Grid>
                  </Grid>
                </MultiSelect>
              </TableHeadCell>
              <TableHeadCell sx={{ width: '213px', p: '16px 0' }}>
                <IconButton
                  onClick={lastSeenHandler}
                  sx={{
                    ml: 0,
                    p: 0,
                    verticalAlign: 'middle',
                    color: 'text.secondary',
                  }}
                >
                  <Icon id="filter" viewBox="0 0 24 24" />
                </IconButton>
                <span style={{ verticalAlign: 'middle' }}>Last seen</span>
              </TableHeadCell>
              <TableHeadCell sx={{ width: '213px', pr: 0 }}>
                Comment
              </TableHeadCell>
              <TableHeadCell sx={{ width: '46px', p: '16px 0' }} />
              <TableHeadCell sx={{ width: '46px', p: '16px 0' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {allResults.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="h3">
                    There are no devices in your account. Please add a device to
                    view it here.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {allResults.length > 0 && sortedAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="h3">
                    There are no devices matching the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {paginatedAgents.map(
              ({
                id,
                uid,
                hostname,
                address,
                comment,
                profile,
                os_info,
                last_seen,
              }) => (
                <AgentTableRow
                  key={`${id}-agent`}
                  uid={uid}
                  device={hostname}
                  address={address}
                  comment={comment}
                  profile={getProfileName(profiles, profile)}
                  os={os_info}
                  lastSeen={last_seen}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedAgents.length > ITEMS_PER_PAGE ? (
        <Grid container sx={{ justifyContent: 'flex-end', pt: 5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={pageHandler}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                slots={{
                  next: NextPageIcon,
                  previous: PreviousPageIcon,
                }}
              />
            )}
          />
        </Grid>
      ) : null}
    </>
  );
};

export default AgentsTable;
